<script setup lang="ts">
import { computed, onMounted } from 'vue'
import {
  ElAlert,
  ElEmpty,
  ElInput,
  ElRadioButton,
  ElRadioGroup,
  ElTabPane,
  ElTabs,
} from 'element-plus'
import ConnectionSection from './ConnectionSection.vue'
import SettingsSection from '~/components/settings/SettingsSection.vue'
import type {
  FilterChip,
  RegionFilter,
} from '~/compositions/useConnectionsFilter'
import { useConnectionsFilter } from '~/compositions/useConnectionsFilter'
import type { ConnectionMetadata } from '~/models/connectionMetadata'
import type { ConnectionType } from '~/services/apiClient'
import { OrganizationSubscription } from '~/services/apiClient'
import { useConnectionsStore } from '~/stores/connections'
import { useUserStore } from '~/stores/user'
import { BRAND_NAME } from '~/constants/branding'

const props = withDefaults(
  defineProps<{
    utasConnectionsOnly?: boolean
    showDirections?: boolean
    context?: 'setup' | 'settings'
    // Show only these connections and none of the filter UI, for fixing a known
    // set of credentials in place (the credential gap prompt). Takes the rows
    // straight from the store, so they stay on screen once the user fixes them.
    connectionTypes?: readonly ConnectionType[]
  }>(),
  {
    utasConnectionsOnly: false,
    showDirections: true,
    context: 'settings',
    connectionTypes: undefined,
  }
)

// Last Free Day values surfaced through UTAS come from Port of LA defaults, so
// we disclaim their informational nature here.
const UTAS_LFD_DISCLAIMER =
  'Last Free Day values are based on default values for the Port of Los Angeles' +
  ' and are for informational purposes only. For contractual Last Free Day,' +
  " please consult your carrier's website for additional information."

const connectionsStore = useConnectionsStore()
const userStore = useUserStore()
const {
  chip,
  region,
  search,
  setChip,
  setRegion,
  setSearch,
  filteredMetas,
  counts,
} = useConnectionsFilter()

const isScopedToConnectionTypes = computed(
  () => props.connectionTypes !== undefined
)

// The filters are shared app-wide state, so whatever chip or search the user left
// on the Settings page must not decide which rows a scoped manager shows.
const visibleMetas = computed((): ConnectionMetadata[] => {
  const scopedTypes = props.connectionTypes
  if (scopedTypes === undefined) return filteredMetas.value
  return connectionsStore.editableConnectionTypeMetasForOrg.filter((m) =>
    scopedTypes.includes(m.type)
  )
})

const userCanEditConnections = computed(() => userStore.canEditConnections)

const showRegionFilter = computed(
  () => !props.utasConnectionsOnly && userStore.isOaklandOrg
)

// `el-segmented` takes an `options` array of `{label, value}`. Keep the
// label strings here so the order + copy lives in one place.
const REGION_OPTIONS: { label: string; value: RegionFilter }[] = [
  { label: 'All regions', value: 'all' },
  { label: 'LA/LB', value: 'lalb' },
  { label: 'Oakland', value: 'oakland' },
]

// Managed metas (e.g. LBCT API) are shown alongside the relevant category but
// are deliberately excluded from `useConnectionsFilter` (counts, error
// callout, needs-attention pinning) since the user can't act on them.
// They render in the standard category sections, not the UTAS-only view.
const managedMetas = computed((): ConnectionMetadata[] => {
  if (props.utasConnectionsOnly || isScopedToConnectionTypes.value) return []
  // Managed entries only show under the `all` chip — they're status-only
  // rows that the user can't act on, so they don't belong under the
  // action-oriented chips (needs_attention / paused / not_used).
  if (chip.value !== 'all') return []
  return connectionsStore.managedConnectionMetas.filter((m) => {
    if (region.value !== 'all' && !m.regions?.includes(region.value))
      return false
    if (
      search.value &&
      !m.label.toLowerCase().includes(search.value.toLowerCase())
    ) {
      return false
    }
    return true
  })
})

// Settings keeps a covered row on All, Paused, and Not used.
// The setup popup counts already skip that row, so the popup list skips it too.
const rowMetas = computed((): ConnectionMetadata[] => {
  if (props.context !== 'setup') return visibleMetas.value
  return visibleMetas.value.filter(
    (meta) => !connectionsStore.isSetupCovered(meta.type)
  )
})

const terminalMetas = computed(() => [
  ...rowMetas.value.filter((m) => m.category === 'terminal_portal'),
  ...managedMetas.value.filter((m) => m.category === 'terminal_portal'),
])

const appointmentMetas = computed(() => [
  ...rowMetas.value.filter((m) => m.category === 'appointment_system'),
  ...managedMetas.value.filter((m) => m.category === 'appointment_system'),
])

const shippingLineMetas = computed(() => [
  ...rowMetas.value.filter((m) => m.category === 'shipping_line_portal'),
  ...managedMetas.value.filter((m) => m.category === 'shipping_line_portal'),
])

const otherMetas = computed(() =>
  rowMetas.value.filter(
    (m) =>
      m.category !== 'terminal_portal' &&
      m.category !== 'appointment_system' &&
      m.category !== 'shipping_line_portal'
  )
)

// True when the active search/filter would render zero rows across every
// category section. Used to show an empty-state message instead of a
// blank page.
const hasNoResults = computed(
  () =>
    !props.utasConnectionsOnly &&
    !isScopedToConnectionTypes.value &&
    terminalMetas.value.length === 0 &&
    appointmentMetas.value.length === 0 &&
    shippingLineMetas.value.length === 0 &&
    otherMetas.value.length === 0
)

const isFilterActive = computed(
  () =>
    chip.value !== 'all' || region.value !== 'all' || search.value.length > 0
)

function clearFilters() {
  setChip('all')
  setRegion('all')
  setSearch('')
}

const handledPercentage = computed(() => {
  if (!counts.value.total) return 0
  // "Handled" = every status the user has intentionally landed on
  // (connected, paused, or marked not-used). Only `failing` and
  // `not_set_up` count as outstanding work.
  const handled =
    counts.value.connected + counts.value.paused + counts.value.not_used
  return Math.round((handled / counts.value.total) * 100)
})

const alertDesc = computed(() => {
  if (props.context === 'setup') {
    return 'Enter your credentials for the terminals and shipping lines you work with. You can mark any you don\'t use as "Not Used" and set them up later.'
  }
  if (props.utasConnectionsOnly) {
    return 'To use the UTAS, you must have valid login credentials for the following accounts.'
  }
  return `To use ${BRAND_NAME}, you must have valid login credentials for the following accounts.`
})

// el-tabs panes — iterating this gives us a `{label, name}` per tab.
// Categories (terminals / appointment systems / shipping lines) are
// expressed as sub-section headers further down the page, so we don't
// repeat them as filter tabs.
const FILTER_TABS: { label: string; name: FilterChip }[] = [
  { label: 'All', name: 'all' },
  { label: 'Needs attention', name: 'needs_attention' },
  { label: 'Paused', name: 'paused' },
  { label: 'Not used', name: 'not_used' },
]

// Label lookup for the empty-state copy (preserves the read-friendly chip
// name when we tell the user "nothing matches '<filter>'").
const CHIP_LABEL_LOOKUP: Record<FilterChip, string> = Object.fromEntries(
  FILTER_TABS.map((t) => [t.name, t.label])
) as Record<FilterChip, string>

onMounted(() => {
  if (userStore.isSubCarrier) return
  connectionsStore.loadConnectionsIfNotLoaded(
    props.utasConnectionsOnly ? OrganizationSubscription.Uas : undefined
  )
})
</script>

<template>
  <div>
    <div v-if="showDirections" class="mb-2">
      <ElAlert title="" type="info" show-icon :closable="false">
        {{ alertDesc }}
      </ElAlert>
    </div>

    <div v-if="utasConnectionsOnly" class="mb-2">
      <ElAlert title="" type="info" show-icon :closable="false">
        {{ UTAS_LFD_DISCLAIMER }}
      </ElAlert>
    </div>

    <!-- Restriction banner if user cannot edit connections -->
    <div v-if="!userCanEditConnections" class="mb-2">
      <ElAlert type="warning" show-icon :closable="false">
        <template #title>
          <span class="font-medium">Connection editing is restricted</span>
        </template>
        Your organization has restricted connection editing to management users
        only. Contact your administrator if you need to make changes.
      </ElAlert>
    </div>

    <!-- Status summary tiles + progress bar. These ride the "rich"
         onboarding view; the settings surface is lean (search + tabs +
         region + rows only). Paused is merged into "Not used or Paused"
         so the four tiles add up to the total without giving the rarely-
         used Paused state its own column. -->
    <div v-if="context === 'setup' && !utasConnectionsOnly" class="mb-4">
      <div class="flex flex-wrap gap-3 mb-3">
        <!-- Connected -->
        <div
          class="flex-1 min-w-[110px] rounded-lg border border-green-200 bg-green-50 px-3 py-2"
        >
          <div class="text-2xl font-bold text-green-700">
            {{ counts.connected }}
          </div>
          <div class="text-xs text-green-600 font-medium mt-0.5">Connected</div>
        </div>
        <!-- Failing -->
        <div
          class="flex-1 min-w-[110px] rounded-lg border border-red-200 bg-red-50 px-3 py-2"
        >
          <div class="text-2xl font-bold text-red-700">
            {{ counts.failing }}
          </div>
          <div class="text-xs text-red-600 font-medium mt-0.5">Failing</div>
        </div>
        <!-- Not set up -->
        <div
          class="flex-1 min-w-[110px] rounded-lg border border-orange-200 bg-orange-50 px-3 py-2"
        >
          <div class="text-2xl font-bold text-orange-700">
            {{ counts.not_set_up }}
          </div>
          <div class="text-xs text-orange-600 font-medium mt-0.5">
            Not set up
          </div>
        </div>
        <!-- Not used or Paused (combined intentional dormant states) -->
        <div
          class="flex-1 min-w-[110px] rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
        >
          <div class="text-2xl font-bold text-gray-600">
            {{ counts.not_used + counts.paused }}
          </div>
          <div class="text-xs text-gray-500 font-medium mt-0.5">
            Not used or Paused
          </div>
        </div>
      </div>
      <!-- Progress bar. "Handled" = every intentional outcome (connected,
           paused, or marked not-used). Same numerator the percentage uses
           so the two never disagree. -->
      <div class="flex justify-between items-center mb-1">
        <span class="text-xs text-gray-500">
          {{ counts.connected + counts.paused + counts.not_used }} of
          {{ counts.total }} handled
        </span>
        <span class="text-xs text-gray-400"> {{ handledPercentage }}% </span>
      </div>
      <div class="w-full bg-gray-100 rounded-full h-1.5">
        <div
          class="bg-green-500 h-1.5 rounded-full transition-all duration-300"
          :style="{ width: `${handledPercentage}%` }"
        />
      </div>
    </div>

    <div
      v-if="!utasConnectionsOnly && !isScopedToConnectionTypes"
      class="mb-3 flex flex-wrap items-center gap-3"
    >
      <ElInput
        class="conn-search"
        :model-value="search"
        placeholder="Search connections…"
        clearable
        @update:model-value="setSearch"
      >
        <template #prefix>
          <i-mdi:magnify class="text-gray-400" />
        </template>
      </ElInput>

      <ElTabs
        class="conn-tabs shrink-0 min-w-0"
        :model-value="chip"
        @update:model-value="setChip($event as FilterChip)"
      >
        <ElTabPane
          v-for="t in FILTER_TABS"
          :key="t.name"
          :label="t.label"
          :name="t.name"
        />
      </ElTabs>

      <!-- `ml-auto` pushes the region toggle to the far right of the row
           regardless of how much horizontal space the tabs take. On
           narrower viewports the row wraps and `ml-auto` keeps the
           region on the right of its own line. -->
      <ElRadioGroup
        v-if="showRegionFilter"
        class="shrink-0 ml-auto"
        :model-value="region"
        aria-label="Region filter"
        @update:model-value="setRegion($event as RegionFilter)"
      >
        <ElRadioButton
          v-for="opt in REGION_OPTIONS"
          :key="opt.value"
          :value="opt.value"
        >
          {{ opt.label }}
        </ElRadioButton>
      </ElRadioGroup>
    </div>

    <div v-loading="connectionsStore.loading">
      <!-- UTAS-only view: single flat list of UTAS connections -->
      <template v-if="utasConnectionsOnly">
        <ConnectionSection
          :utas-connections-only="true"
          :connection-meta-list="rowMetas"
          :user-can-edit-connections="userCanEditConnections"
        />
      </template>

      <!-- Standard view: category sections. Failing/not-set-up rows are
           surfaced via the "Needs attention" tab in the filter strip and
           via each row's red/orange border treatment; no separate pinned
           section. -->
      <!-- Scoped to a few credentials (the credential prompt): just the rows. The
           category headings are for browsing, and here they only add words -->
      <template v-else-if="isScopedToConnectionTypes">
        <ConnectionSection
          :utas-connections-only="false"
          :connection-meta-list="rowMetas"
          :user-can-edit-connections="userCanEditConnections"
          fix-only
        />
      </template>

      <template v-else>
        <SettingsSection
          v-if="terminalMetas.length > 0"
          title="Terminals + Tracking"
        >
          <ConnectionSection
            :utas-connections-only="false"
            :connection-meta-list="terminalMetas"
            :user-can-edit-connections="userCanEditConnections"
          />
        </SettingsSection>

        <SettingsSection
          v-if="appointmentMetas.length > 0"
          title="Appointment Systems"
        >
          <ConnectionSection
            :utas-connections-only="false"
            :connection-meta-list="appointmentMetas"
            :user-can-edit-connections="userCanEditConnections"
          />
        </SettingsSection>

        <SettingsSection
          v-if="shippingLineMetas.length > 0"
          title="Non-Terminal Tracking"
        >
          <ConnectionSection
            :utas-connections-only="false"
            :connection-meta-list="shippingLineMetas"
            :user-can-edit-connections="userCanEditConnections"
          />
        </SettingsSection>

        <SettingsSection v-if="otherMetas.length > 0" title="Other">
          <ConnectionSection
            :utas-connections-only="false"
            :connection-meta-list="otherMetas"
            :user-can-edit-connections="userCanEditConnections"
          />
        </SettingsSection>

        <!-- Empty state: nothing matches the active filter / search -->
        <ElEmpty
          v-if="hasNoResults && !connectionsStore.loading"
          :image-size="80"
          description="No connections match"
        >
          <template #description>
            <div class="text-sm font-medium text-gray-700 mb-1">
              No connections match
            </div>
            <div class="text-xs text-gray-500 max-w-sm">
              <template v-if="search">
                No portal name matches
                <span class="font-medium text-gray-700">"{{ search }}"</span>
                <template v-if="chip !== 'all'">
                  under the
                  <span class="font-medium text-gray-700">
                    {{ CHIP_LABEL_LOOKUP[chip] }}
                  </span>
                  category.
                </template>
              </template>
              <template v-else-if="chip !== 'all'">
                Nothing falls under
                <span class="font-medium text-gray-700">
                  {{ CHIP_LABEL_LOOKUP[chip] }}
                </span>
                right now.
              </template>
              <template v-else> There are no connections to show. </template>
            </div>
          </template>
          <el-button
            v-if="isFilterActive"
            text
            type="primary"
            @click="clearFilters"
          >
            Clear filters
          </el-button>
        </ElEmpty>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.conn-search {
  width: 14rem;
  flex-shrink: 0;
}

// el-tabs is used purely as a tab strip here (filter state lives in
// `chip`, the rows are rendered below the filter bar). Hide the empty
// tab content panel and drop the strip's bottom border so it sits
// cleanly inline with the search input and segmented control.
.conn-tabs {
  :deep(.el-tabs__header) {
    margin: 0;
    border-bottom: 0;
  }
  :deep(.el-tabs__nav-wrap)::after {
    display: none;
  }
  :deep(.el-tabs__content) {
    display: none;
  }
}
</style>
